/**
 * 👑 CROWN SHOP - Boutique de couronnes
 * Composant intégrable pour acheter des couronnes via Stripe
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Sparkles, Gift, X, CreditCard, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// 🔧 FIX: Utiliser l'origine courante pour heartbeats.fr pour éviter les problèmes de config
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // Si on est sur heartbeats.fr, utiliser l'origine directement
  if (hostname === 'heartbeats.fr' || hostname === 'www.heartbeats.fr') {
    return window.location.origin;
  }
  // Sinon utiliser la variable d'environnement
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const API_URL = getApiUrl();

// Packages de couronnes avec visuels
const CROWN_PACKAGES = [
  {
    id: 'crowns_small',
    name: 'Pack Starter',
    crowns: 100,
    bonus: 0,
    price: 4.99,
    color: 'from-amber-400 to-yellow-500',
    popular: false
  },
  {
    id: 'crowns_medium',
    name: 'Pack Popular',
    crowns: 500,
    bonus: 50,
    price: 19.99,
    color: 'from-purple-400 to-pink-500',
    popular: true
  },
  {
    id: 'crowns_large',
    name: 'Pack Premium',
    crowns: 1000,
    bonus: 200,
    price: 34.99,
    color: 'from-cyan-400 to-blue-500',
    popular: false
  },
  {
    id: 'crowns_mega',
    name: 'Pack Royal',
    crowns: 5000,
    bonus: 1500,
    price: 149.99,
    color: 'from-amber-500 to-orange-600',
    popular: false,
    bestValue: true
  }
];

const CrownShop = ({ isOpen, onClose, customerId, customerEmail, currentCrowns = 0 }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Acheter des couronnes - Utilise axios pour éviter les problèmes de fetch
  const handlePurchase = async (pkg) => {
    if (loading) return;
    
    setLoading(true);
    setSelectedPackage(pkg.id);
    
    try {
      const response = await axios.post(`${API_URL}/api/payments/checkout/create`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
        customer_id: customerId,
        customer_email: customerEmail,
        metadata: {
          source: 'crown_shop',
          crowns: String(pkg.crowns),
          bonus: String(pkg.bonus)
        }
      });

      // Axios parse automatiquement le JSON
      if (response.data && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de paiement manquante');
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur de paiement';
      toast.error(errorMessage);
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-xl p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Boutique</h2>
                  <p className="text-white/60 text-sm">Acheter des couronnes</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-white/60" />
              </button>
            </div>

            {/* Current balance */}
            <div className="mt-4 flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
              <Crown className="w-5 h-5 text-amber-400" />
              <span className="text-white/60">Solde actuel:</span>
              <span className="text-white font-bold">{currentCrowns.toLocaleString()}</span>
              <span className="text-amber-400">couronnes</span>
            </div>
          </div>

          {/* Packages Grid */}
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CROWN_PACKAGES.map((pkg) => (
                <motion.button
                  key={pkg.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handlePurchase(pkg)}
                  disabled={loading}
                  className={`relative bg-white/5 rounded-2xl p-5 text-left border transition-all ${
                    pkg.popular ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-white/10 hover:border-white/30'
                  } ${loading && selectedPackage === pkg.id ? 'opacity-70' : ''}`}
                >
                  {/* Popular badge */}
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      POPULAIRE
                    </div>
                  )}

                  {/* Best value badge */}
                  {pkg.bestValue && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      MEILLEUR RAPPORT
                    </div>
                  )}

                  {/* Crown icon */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${pkg.color} rounded-xl flex items-center justify-center mb-3`}>
                    <Crown className="w-8 h-8 text-white" />
                  </div>

                  {/* Package name */}
                  <h3 className="text-lg font-bold text-white mb-1">{pkg.name}</h3>

                  {/* Crowns amount */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl font-black text-white">{pkg.crowns.toLocaleString()}</span>
                    {pkg.bonus > 0 && (
                      <span className="text-green-400 font-semibold">+{pkg.bonus}</span>
                    )}
                    <Crown className="w-5 h-5 text-amber-400" />
                  </div>

                  {/* Bonus badge */}
                  {pkg.bonus > 0 && (
                    <div className="flex items-center gap-1 text-green-400 text-sm mb-3">
                      <Gift className="w-4 h-4" />
                      <span>{pkg.bonus} couronnes bonus !</span>
                    </div>
                  )}

                  {/* Price */}
                  <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/10">
                    <span className="text-2xl font-bold text-white">{pkg.price.toFixed(2)}€</span>
                    {loading && selectedPackage === pkg.id ? (
                      <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                    ) : (
                      <CreditCard className="w-6 h-6 text-white/40" />
                    )}
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Security note */}
            <div className="mt-6 flex items-center justify-center gap-2 text-white/40 text-sm">
              <Check className="w-4 h-4" />
              <span>Paiement sécurisé par Stripe</span>
              <Sparkles className="w-4 h-4 text-amber-400" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CrownShop;
